export interface WorksheetColumn {
  id: string;
  name: string;
  values: string[];
  nonEmptyCount: number;
  numericValues: number[];
  numericCount: number;
  missingCount: number;
  uniqueValues: string[];
}

export interface ParsedWorksheet {
  columnNames: string[];
  rows: string[][];
  columns: WorksheetColumn[];
  numericColumns: WorksheetColumn[];
  rowCount: number;
  columnCount: number;
  hasHeaders: boolean;
}

const splitLine = (line: string, delimiterMode: string, singleLineVector: boolean) => {
  if (singleLineVector) {
    return line.split(/[\s,]+/).filter(Boolean).map((token) => [token]);
  }

  if (delimiterMode === 'tab' || (delimiterMode === 'auto' && line.includes('\t'))) {
    return [line.split(/\t+/).map((cell) => cell.trim())];
  }

  if (delimiterMode === 'comma') {
    return [line.split(/\s*,\s*/).map((cell) => cell.trim())];
  }

  if (delimiterMode === 'space') {
    return [line.split(/\s+/).map((cell) => cell.trim())];
  }

  if (line.includes(',')) {
    return [line.split(/\s*,\s*/).map((cell) => cell.trim())];
  }

  return [line.split(/\s+/).map((cell) => cell.trim())];
};

const isNumericCell = (value: string) => {
  if (!value.trim()) return false;
  return Number.isFinite(Number(value));
};

export const parseWorksheet = (input: string, delimiterMode: string): ParsedWorksheet => {
  const lines = input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return {
      columnNames: [],
      rows: [],
      columns: [],
      numericColumns: [],
      rowCount: 0,
      columnCount: 0,
      hasHeaders: false,
    };
  }

  const singleLineVector = lines.length === 1 && !lines[0].includes('\t');
  const parsedLines = lines.flatMap((line) => splitLine(line, delimiterMode, singleLineVector));
  const columnCount = Math.max(...parsedLines.map((row) => row.length), 1);
  const normalizedRows = parsedLines.map((row) =>
    Array.from({ length: columnCount }, (_, index) => row[index] ?? ''),
  );

  const firstRow = normalizedRows[0] ?? [];
  const nonNumericInFirstRow = firstRow.filter((cell) => cell && !isNumericCell(cell)).length;
  const hasHeaders = !singleLineVector && normalizedRows.length > 1 && nonNumericInFirstRow >= Math.ceil(columnCount / 2);

  const columnNames = hasHeaders
    ? firstRow.map((cell, index) => cell || `C${index + 1}`)
    : Array.from({ length: columnCount }, (_, index) => `C${index + 1}`);

  const dataRows = hasHeaders ? normalizedRows.slice(1) : normalizedRows;
  const columns = columnNames.map((name, index) => {
    const values = dataRows.map((row) => row[index] ?? '');
    const nonEmptyValues = values.filter((cell) => cell.trim() !== '');
    const numericValues = nonEmptyValues.map((cell) => Number(cell)).filter((value) => Number.isFinite(value));
    const uniqueValues = Array.from(new Set(nonEmptyValues)).slice(0, 50);

    return {
      id: `c${index + 1}`,
      name,
      values,
      nonEmptyCount: nonEmptyValues.length,
      numericValues,
      numericCount: numericValues.length,
      missingCount: Math.max(nonEmptyValues.length - numericValues.length, 0),
      uniqueValues,
    };
  });

  return {
    columnNames,
    rows: dataRows,
    columns,
    numericColumns: columns.filter((column) => column.numericCount > 0),
    rowCount: dataRows.length,
    columnCount,
    hasHeaders,
  };
};
